import React from 'react';

const About = () => {
  return (
    <section id="about" className="bg-white py-20 lg:py-28">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-16 mb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">About Us</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A focused consultancy for teams that need real tools built, not slide decks.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Custom Applications */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          <div className="order-2 lg:order-1">
            <div className="relative h-80 lg:h-96 overflow-hidden rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-300">
              <img
                src="https://images.squarespace-cdn.com/content/v1/6415faf82502b351b9d7d8e7/1679165531920-820QWCZIZAU2PTNMJ5T8/unsplash-image-XmZ4GDAp9G0.jpg"
                alt="Custom dashboard and data application interface"
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent"></div>
            </div>
          </div>

          <div className="order-1 lg:order-2 text-center lg:text-left">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-xl mb-6">
              <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Custom Applications</h3>
            <div className="w-16 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mb-6 mx-auto lg:mx-0"></div>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Off-the-shelf software rarely fits the exact way your team works.
              We build focused, purpose-built web applications, internal tools, and
              integrations that match your workflow — not the other way around.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Every build prioritizes security, maintainability, and handoff —
              so the tool keeps running long after the engagement ends.
            </p>
          </div>
        </div>

        {/* Consulting + Strategy */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-xl mb-6">
              <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Consulting &amp; Strategy</h3>
            <div className="w-16 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mb-6 mx-auto lg:mx-0"></div>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Before building anything, we spend time understanding how your data flows
              today — where it lives, who uses it, and where the real bottlenecks are.
              That honest diagnosis is the difference between a tool that gets adopted
              and one that collects dust.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              You'll leave every engagement knowing exactly what we built,
              why we built it that way, and how your team can extend it.
            </p>
          </div>

          <div>
            <div className="relative h-80 lg:h-96 overflow-hidden rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-300">
              <img
                src="https://images.squarespace-cdn.com/content/v1/6415faf82502b351b9d7d8e7/1679164294986-UOSYL6YX7LLI6S878EG3/unsplash-image-5fNmWej4tAA.jpg"
                alt="Team collaborating on data strategy"
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent"></div>
            </div>
          </div>
        </div>

        {/* Analytics + AI */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
            <div className="relative h-80 lg:h-96 overflow-hidden rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-300">
              <img
                src="https://images.squarespace-cdn.com/content/v1/6415faf82502b351b9d7d8e7/1679163937481-YWY7IB88MUY025IGE4JP/unsplash-image-hpjSkU2UYSU.jpg"
                alt="Data visualization and analytics"
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent"></div>
            </div>
          </div>

          <div className="order-1 lg:order-2 text-center lg:text-left">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-xl mb-6">
              <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Analytics &amp; AI</h3>
            <div className="w-16 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mb-6 mx-auto lg:mx-0"></div>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Dashboards that leadership actually opens. AI tools that replace
              real hours of repetitive work. We focus on the mundane, high-value
              use cases — not moonshots — because that's where the measurable
              return lives for most teams.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Every analytics and AI solution we deliver includes the data
              plumbing, access controls, and monitoring to run reliably in
              production — not just a demo notebook.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
